import * as Yup from "yup";
import { YupDefaultProps, YupSelectProps, YupStringProps } from "./types";
import { string, array, boolean, ref } from "yup";
import { Option, SerializableFile } from "../types";
import moment from "moment";
import {
  fieldRequiredMessage,
  numberRequiredMessage,
  maxMessage,
  specialCharactersMessage,
  lettersMessage,
  alphanumericStringMessage,
  emailFormatMessage,
  phoneNumberRequiredMessage,
  dniRequiredMessage,
  tramitNumberRequiredMessage,
  invalidDocumentFormatMessage,
  invalidCuitPrefixMessage,
  cuitRequiredMessage,
  cbuRequiredMessage,
  tokenMessage,
  optionRequiredMessage,
  pastDateRequiredMessage,
  futureDateRequiredMessage,
  smallerThanMessage,
  largerThanMessage,
  dateFormatMessage,
  percentagesAddUpMessage,
  percentageRangeMessage,
  emailNotMatchMessage,
  ssnRequiredMessage,
  differentCuitMessage,
  duplicateCuitMessage,
  duplicateEmailMessage,
  percentageGreaterThanZeroMessage,
  invalidCuitPFPrefixMessage,
  invalidCuitPJPrefixMessage,
} from "../i18n/validationMessages";

export const validationsForLanguage = (language?: "es" | "en") => {
  const numberRequired = string()
    .matches(/^[0-9]*$/, numberRequiredMessage[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const yupString = ({
    min,
    max,
    required,
    minMessage,
    maxMessage,
    requiredMessage,
    regex,
    regexMessage,
  }: YupStringProps) => {
    let schema = Yup.string();
    if (min) schema = schema.min(min, minMessage);
    if (max) schema = schema.max(max, maxMessage);
    if (regex) schema = schema.matches(regex, regexMessage);
    if (required) schema = schema.required(requiredMessage);
    return schema;
  };

  const yupSelect = ({
    type,
    options,
    optionsMessage,
    required,
    requiredMessage,
  }: YupSelectProps) => {
    let schema;

    const optionsSchema = Yup.string().oneOf(
      options.map((option) => option.value),
      optionsMessage
    );

    if (type === "single") {
      schema = optionsSchema;
    }

    if (type === "multiple") {
      schema = Yup.array().of(optionsSchema);
    }

    if (required && schema) {
      schema = schema.required(requiredMessage);
    }

    return schema;
  };

  const YupNumber = ({
    min,
    max,
    required,
    minMessage,
    maxMessage,
    requiredMessage,
  }: YupDefaultProps) => {
    let schema = Yup.number();

    if (min) schema = schema.min(min, minMessage);
    if (max) schema = schema.max(max, maxMessage);
    if (required) schema = schema.required(requiredMessage);
    return schema;
  };

  /* New Email Verification Regex for Yup.string().email function */
  Yup.addMethod(Yup.string, "email", function validateEmail(message) {
    /* Required <something> @ <something> . <something> */
    const myEmailRegex = /^((?!\.)[\w\-_.+]*[^.])(@[\w-]+)(\.\w+(\.\w+)?[^.\W])$/;

    return this.matches(myEmailRegex, {
      message,
      name: "email",
      excludeEmptyString: true,
    });
  });

  const stringRequired = string().required(
    fieldRequiredMessage[language ?? "es"]
  );
  const stringNotRequired = string().optional();

  const percentageRequired = string()
    .test(
      "greaterThanZero",
      percentageGreaterThanZeroMessage[language ?? "es"],
      (value) => {
        if (!value) return false;
        return parseFloat(value) > 0;
      }
    )
    .test(
      "lessThanOrEqual100",
      percentageRangeMessage[language ?? "es"],
      (value) => {
        if (!value) return false;
        return parseFloat(value) <= 100;
      }
    )
    .required(fieldRequiredMessage[language ?? "es"]);

  const numbersOnly = string().matches(
    /^[0-9]*$/,
    numberRequiredMessage[language ?? "es"]
  );

  const smallStringRequired = string()
    .trim()
    .max(50, maxMessage(50)[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const smallString = string()
    .max(50, maxMessage(50)[language ?? "es"])
    .nullable();

  const mediumStringRequired = string()
    .max(100, maxMessage(100)[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const largeStringRequired = string()
    .max(200, maxMessage(200)[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const lettersRequired = string()
    .matches(/^[a-zA-Z\s+]*$/, lettersMessage[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const noSpecialCharactersStringRequired = string()
    .matches(/^[a-zA-Z0-9+]+$/, specialCharactersMessage[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const alphanumericStringRequired = string()
    .matches(/^[a-zA-Z0-9\s+]*$/, alphanumericStringMessage[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const lettersOnly = string().matches(
    /^[a-zA-Z\s+]*$/,
    lettersMessage[language ?? "es"]
  );

  const checkboxRequired = boolean().oneOf(
    [true],
    fieldRequiredMessage[language ?? "es"]
  );

  const checkbox = boolean().notRequired();

  // Personal information
  const emailRequired = string()
    .email(emailFormatMessage[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const internationalPhone = string()
    .min(9, phoneNumberRequiredMessage[language ?? "es"])
    .max(15, phoneNumberRequiredMessage[language ?? "es"]);

  const internationalPhoneRequired = string()
    .min(9, phoneNumberRequiredMessage[language ?? "es"])
    .max(15, phoneNumberRequiredMessage[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const confirmEmailRequired = string()
    .oneOf([ref("email")], emailNotMatchMessage[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const phone = string()
    .required(fieldRequiredMessage[language ?? "es"])
    .test("is-valid-phone", fieldRequiredMessage[language ?? "es"], (value) => {
      if (!value) {
        return false; // Required
      }
      return true;
    })
    .test(
      "is-valid-phone",
      phoneNumberRequiredMessage[language ?? "es"],
      (value) => {
        if (!value) {
          return false; // Required
        }
        // Checks if it starts with "54" and has 12 digits
        if (value.startsWith("54")) {
          return value.length === 12;
        } else return true;
      }
    )
    .test(
      "is-valid-phone",
      phoneNumberRequiredMessage[language ?? "es"],
      (value) => {
        if (!value) {
          return false; // If the value is empty, it is not valid
        }
        if (!value.startsWith("54")) {
          return value.length >= 9 && /^\d+$/.test(value);
        }
        return true;
      }
    );

  const dniRequired = string()
    .matches(/^[0-9]{7,8}$/, dniRequiredMessage[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const tramitNumberRequired = string()
    .matches(/^[0-9]{11}$/, tramitNumberRequiredMessage[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const documentNumberRequired = string()
    .required(fieldRequiredMessage[language ?? "es"])
    .matches(/^\d+$/, invalidDocumentFormatMessage[language ?? "es"]);

  const cuitRequired = string()
    .matches(/^(20|23|24|27|30|33)/, invalidCuitPrefixMessage[language ?? "es"])
    .matches(/^[0-9]{11}$/, cuitRequiredMessage[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const cuitPJRequired = string()
    .matches(/^(30|33)/, invalidCuitPJPrefixMessage[language ?? "es"])
    .matches(/^[0-9]{11}$/, cuitRequiredMessage[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const cuitPFRequired = string()
    .matches(/^(20|23|24|27)/, invalidCuitPFPrefixMessage[language ?? "es"])
    .matches(/^[0-9]{11}$/, cuitRequiredMessage[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const cuitSpouse = string()
    .matches(/^(20|23|24|27|30|33)/, invalidCuitPrefixMessage[language ?? "es"])
    .matches(/^[0-9]{11}$/, cuitRequiredMessage[language ?? "es"])
    .notOneOf([ref("cuit")], differentCuitMessage[language ?? "es"]);

  const shareholderCuitSpouse = string()
    .matches(/^(20|23|24|27|30|33)/, invalidCuitPrefixMessage[language ?? "es"])
    .matches(/^[0-9]{11}$/, cuitRequiredMessage[language ?? "es"])
    .notOneOf([ref("taxId")], differentCuitMessage[language ?? "es"]);

  const cbuNotRequired = string()
    .matches(/^[0-9]{22}$/, cbuRequiredMessage[language ?? "es"])
    .nullable();
  const cbuRequired = cbuNotRequired.required(
    fieldRequiredMessage[language ?? "es"]
  );

  const radioOrSingleSelectRequired = (options: Option[]) =>
    string()
      .oneOf(options.map((option) => option.value))
      .required(fieldRequiredMessage[language ?? "es"]);

  const tokenRequired = alphanumericStringRequired
    .min(7, tokenMessage[language ?? "es"])
    .max(7, tokenMessage[language ?? "es"]);

  const zipCodeRequired = string()
    .matches(/^[a-zA-Z0-9\s-]+$/, specialCharactersMessage[language ?? "es"])
    .max(50, maxMessage(50)[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const shareholderCuitPJRequired = (shareholdersCuit: string[]) =>
    cuitPJRequired.notOneOf(
      shareholdersCuit,
      duplicateCuitMessage[language ?? "es"]
    );
  const shareholderCuitPFRequired = (shareholdersCuit: string[]) =>
    cuitPFRequired.notOneOf(
      shareholdersCuit,
      duplicateCuitMessage[language ?? "es"]
    );
  const shareholderNifRequired = (shareholdersCuit: string[]) =>
    stringRequired.notOneOf(
      shareholdersCuit,
      "No se puede utilizar la misma identificación fiscal para más de un accionista"
    );

  const signatoryEmailRequired = (signatoriesEmail: string[]) =>
    string()
      .email(emailFormatMessage[language ?? "es"])
      .required(fieldRequiredMessage[language ?? "es"])
      .notOneOf(signatoriesEmail, duplicateEmailMessage[language ?? "es"]);

  const multipleSelectRequired = (options: Option[]) => {
    const optionsSchema = Yup.string().oneOf(
      options.map((option) => option.value),
      optionRequiredMessage[language ?? "es"]
    );

    return array()
      .of(optionsSchema)
      .required(fieldRequiredMessage[language ?? "es"]);
  };

  const createFileSchema = (
    supportedFormats: string[],
    maxFileSize: number,
    isRequired: boolean,
    minFileSize?: number
  ) => {
    const maxFileSizeConvertedToBytes = maxFileSize * 1024 * 1024; // Convert to bytes
    const minFileSizeConvertedToBytes = minFileSize
      ? minFileSize * 1024 * 1024
      : 0;
    let schema = Yup.mixed()
      .test("fileFormat", "FORMAT-ERROR", (value) => {
        const file = value as SerializableFile;
        if (!value || !file?.type) return false;
        return supportedFormats.includes(file.type);
      })
      .test("fileSize", "SIZE-ERROR", (value) => {
        const file = value as SerializableFile;
        if (!value || !file?.size) return false;
        return (
          (file.size < maxFileSizeConvertedToBytes) ||
          typeof value === "number"
        );
      })
      .test("fileSize", "MIN-SIZE-ERROR", (value) => {
        const file = value as SerializableFile;
        if (!value || !file?.size) return false;
        return (
          (file.size >= minFileSizeConvertedToBytes) ||
          typeof value === "number"
        );
      });

    if (isRequired) {
      schema = schema.required(fieldRequiredMessage[language ?? "es"]);
    }

    return schema;
  };

  const smallerNumberRequiered = ({
    than,
    message,
    orEqual,
  }: {
    // Field name to compare to. Relative, must be sibling
    than: string;
    message?: string;
    orEqual?: boolean;
  }) =>
    string()
      .required(fieldRequiredMessage[language ?? "es"])
      .test(
        "smallerThan",
        message ?? smallerThanMessage(),
        (value, context) => {
          const thanValue = context.parent[than];
          const thanFilled = !!thanValue;

          const smallerThan = orEqual
            ? parseFloat(value ?? "") <= parseFloat(thanValue)
            : parseFloat(value ?? "") < parseFloat(thanValue);

          return !thanFilled || smallerThan;
        }
      );

  const largerNumberRequiered = ({
    than,
    message,
    orEqual,
  }: {
    // Field name to compare to. Relative, must be sibling
    than: string;
    message?: string;
    orEqual?: boolean;
  }) =>
    string()
      .required(fieldRequiredMessage[language ?? "es"])
      .test("largerThan", message ?? largerThanMessage(), (value, context) => {
        const thanValue = context.parent[than];
        const thanFilled = !!thanValue;

        const largerThan = orEqual
          ? parseFloat(value ?? "") >= parseFloat(thanValue)
          : parseFloat(value ?? "") > parseFloat(thanValue);

        return !thanFilled || largerThan;
      });

  const percentagesAddUp = (fieldNames: string[]) =>
    string().test(
      "percentagesAddUp",
      percentagesAddUpMessage[language ?? "es"],
      (value, context) => {
        const validPercentage =
          parseFloat(value ?? "0") >= 0 && parseFloat(value ?? "0") <= 100;

        const allFilled = fieldNames.every(
          (name) => !!context.parent[name] || context.parent[name] === "0"
        );

        const percentageTotal = fieldNames
          .filter((name) => !!context.parent[name])
          .map((name) => context.parent[name])
          .reduce(
            (total, percentage: string) =>
              total + parseFloat(percentage.replace(",", ".") ?? "0"),
            0.0
          );

        const addsUp = Math.abs(percentageTotal - 100) < 0.1;

        return validPercentage && (!allFilled || addsUp);
      }
    );

  const fileNotRequired = (
    supportedFormats: string[],
    maxFileSize: number,
    minFileSize?: number
  ) => createFileSchema(supportedFormats, maxFileSize, false, minFileSize);

  const filesNotRequired = (supportedFormats: string[], maxFileSize: number) =>
    Yup.array().of(fileNotRequired(supportedFormats, maxFileSize)).nullable();

  const fileRequired = (
    supportedFormats: string[],
    maxFileSize: number,
    minFileSize?: number
  ) => createFileSchema(supportedFormats, maxFileSize, true, minFileSize);

  const filesRequired = (supportedFormats: string[], maxFileSize: number) =>
    Yup.array()
      .of(fileRequired(supportedFormats, maxFileSize))
      .required(fieldRequiredMessage[language ?? "es"]);

  const previousDateRequired = string()
    .test("format", dateFormatMessage[language ?? "es"], (value) => {
      if (!value) return false;
      // Check if the value matches DD/MM/YYYY format
      const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
      return dateRegex.test(value);
    })
    .test(
      "previousDate",
      pastDateRequiredMessage[language ?? "es"],
      (value) => {
        if (!value) return false;
        // Only check date if format is correct
        const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
        if (!dateRegex.test(value)) return false;

        const date = moment(value, "DD/MM/YYYY").toDate();
        const currentDate = moment().toDate();
        return date <= currentDate;
      }
    )
    .required(fieldRequiredMessage[language ?? "es"]);

  const futureDateRequired = string()
    .test("format", dateFormatMessage[language ?? "es"], (value) => {
      if (!value) return false;
      // Check if the value matches DD/MM/YYYY format
      const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
      return dateRegex.test(value);
    })
    .test(
      "futureDate",
      futureDateRequiredMessage[language ?? "es"],
      (value) => {
        if (!value) return false;
        // Only check date if format is correct
        const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
        if (!dateRegex.test(value)) return false;

        const date = moment(value, "DD/MM/YYYY").toDate();
        const currentDate = moment().toDate();
        return date > currentDate;
      }
    )
    .required(fieldRequiredMessage[language ?? "es"]);

  // Address is an object of type InputAddressProps.onValueChange data
  // const addressShape = {
  //   formattedAddress: Yup.object().shape({
  //     street: string(),
  //     streetNumber: string(),
  //     city: string(),
  //     state: string(),
  //     country: string(),
  //     postalCode: string(),
  //   }),
  //   fullData: Yup.object().shape({
  //     prediction: Yup.object(),
  //     place: Yup.object(),
  //   }),
  // };

  //  const addressRequired = Yup.object()
  //   .shape(addressShape)
  //   .required(fieldRequiredMessage);

  const anyCheckboxCheckedRequired = array()
    .min(1, optionRequiredMessage[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const floatRequired = string().matches(
    /^[0-9,.]*$/,
    numberRequiredMessage[language ?? "es"]
  );

  const ssnRequired = string()
    .matches(/^[0-9]{9}$/, ssnRequiredMessage[language ?? "es"])
    .required(fieldRequiredMessage[language ?? "es"]);

  const dateStringRequired = string()
    .test("format", dateFormatMessage[language ?? "es"], (value) => {
      if (!value) return false;
      // Check if the value matches YYYY-MM-DD format
      const dateRegex = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
      return dateRegex.test(value);
    })
    .required(fieldRequiredMessage[language ?? "es"]);

  const pastDateStringValidation = dateStringRequired.test(
    "isPastDate",
    pastDateRequiredMessage[language ?? "es"],
    (value) => {
      if (!value) return false;
      const date = moment(value, "YYYY-MM-DD").startOf("day");
      const currentDate = moment().startOf("day");
      return date.isBefore(currentDate);
    }
  );

  return {
    numberRequired,
    yupString,
    yupSelect,
    YupNumber,
    stringRequired,
    stringNotRequired,
    numbersOnly,
    smallStringRequired,
    smallString,
    mediumStringRequired,
    largeStringRequired,
    alphanumericStringRequired,
    noSpecialCharactersStringRequired,
    lettersRequired,
    lettersOnly,
    checkboxRequired,
    checkbox,
    emailRequired,
    internationalPhoneRequired,
    internationalPhone,
    phone,
    dniRequired,
    tramitNumberRequired,
    documentNumberRequired,
    cuitRequired,
    cbuNotRequired,
    cbuRequired,
    radioOrSingleSelectRequired,
    tokenRequired,
    zipCodeRequired,
    multipleSelectRequired,
    fileNotRequired,
    fileRequired,
    previousDateRequired,
    futureDateRequired,
    smallerNumberRequiered,
    anyCheckboxCheckedRequired,
    largerNumberRequiered,
    percentagesAddUp,
    percentageRequired,
    confirmEmailRequired,
    floatRequired,
    ssnRequired,
    cuitSpouse,
    cuitPFRequired,
    shareholderCuitSpouse,
    shareholderCuitPJRequired,
    shareholderCuitPFRequired,
    signatoryEmailRequired,
    shareholderNifRequired,
    filesNotRequired,
    filesRequired,
    dateStringRequired,
    pastDateStringValidation,
  };
};

export const {
  numberRequired,
  yupString,
  yupSelect,
  YupNumber,
  stringRequired,
  stringNotRequired,
  numbersOnly,
  smallStringRequired,
  smallString,
  mediumStringRequired,
  largeStringRequired,
  alphanumericStringRequired,
  noSpecialCharactersStringRequired,
  lettersRequired,
  lettersOnly,
  checkboxRequired,
  checkbox,
  emailRequired,
  internationalPhoneRequired,
  internationalPhone,
  phone,
  dniRequired,
  tramitNumberRequired,
  documentNumberRequired,
  cuitRequired,
  cbuNotRequired,
  cbuRequired,
  radioOrSingleSelectRequired,
  tokenRequired,
  zipCodeRequired,
  multipleSelectRequired,
  fileNotRequired,
  fileRequired,
  previousDateRequired,
  futureDateRequired,
  smallerNumberRequiered,
  percentagesAddUp,
  percentageRequired,
  confirmEmailRequired,
  floatRequired,
  ssnRequired,
  cuitSpouse,
  cuitPFRequired,
  shareholderCuitSpouse,
  shareholderCuitPFRequired,
  shareholderCuitPJRequired,
  signatoryEmailRequired,
  shareholderNifRequired,
  filesNotRequired,
  filesRequired,
  dateStringRequired,
  pastDateStringValidation,
} = validationsForLanguage("es");
